import React, { useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  StyledReqElement,
  StyledReqText,
} from './ResetPasswordConfirm.styles';
import api from '../../api/base';
import { changePass, confirmPass } from '../../api/paths';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { Box } from '@mui/system';
import { useSelector } from 'react-redux';
import { CommonPagesWrapper, CustomBasicInput, LoginButton, LoginFormWrapper, TextWrapper } from '../Login/Login.styles';
import { ReactComponent as ArrowLeft } from '../../assets/icons/base/arrow-left.svg';
import { BackLoginText } from '../../components/VerifyOtp/VerifyOtp.styles';
import { RootState } from '../../redux/store';
import useError from '../../hooks/useError';
import { MainTheme } from '../../theme';
import LoginContainer from '../Login/LoginContainer';

const ResetPasswordConfirm = () => {
  const navigate = useNavigate();
  const [handleError] = useError();

  const [searchParams] = useSearchParams();

  const accessToken = useSelector((store: RootState) => store.user.tokens.access);

  const uid = searchParams.get('uid');
  const token = searchParams.get('token');

  const [currentPass, setCurrentPass] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [newConfirm, setNewConfirm] = useState<string>('');

  const [matched, setMatched] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [errors, setErrors] = useState<Record<string, boolean>>({
    digits: true,
    specialCharacter: true,
    upperCase: true,
    lowerCase: true,
    length: true,
  });

  const [resetDone, setResetDone] = useState(false);

  const newPassApi = async (e: any) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (accessToken) {
        await api.post(changePass, { current_password: currentPass, new_password: newPassword }, { headers: { Authorization: 'Bearer 123' } });
      } else {
        await api.post(confirmPass, { uid, token, new_password: newPassword });
      }
      setResetDone(true);
    } catch (error: any) {
      handleError(error);
      setLoading(false);
    }
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const { value } = e.target;

    const newErrors: Record<string, boolean> = {};

    if (!((/\d/).test(value))) {
      newErrors.digits = true;
    }

    if ((!(/[~!@#$%^&*()_+{}\\":;'[\].]/g).test(value))) {
      newErrors.specialCharacter = true;
    }

    if ((!(/[A-Z]/).test(value))) {
      newErrors.upperCase = true;
    }

    if (!((/.*[^A-Z]{3}/).test(value))) {
      newErrors.lowerCase = true;
    }

    if (value.length < 8) {
      newErrors.length = true;
    }

    setMatched(value === newConfirm);

    setNewPassword(value);
    setErrors(newErrors);
  };

  const handleConfirmOnChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const { value } = e.target;

    setMatched(value === newPassword);
    setNewConfirm(value);
  };

  if (resetDone) {
    return (
      <MainTheme forceTheme='dark'>
        <CommonPagesWrapper>
          <LoginContainer>
            <LoginFormWrapper onSubmit={newPassApi}>
              <TextWrapper gap="8px">
                <Typography variant="dsm" color={(t) => t.palette.base.bodyText} fontWeight="600">
                  Password reset
                </Typography>
                <Typography variant="tmd" color={(t) => t.palette.grey[500]}>
                  Your password has been successfully reset.{!accessToken && ' Click below to log in.'}
                </Typography>
              </TextWrapper>
              <Grid width={'100%'}
                item
                xs={12}
                mt={'32px'}>
                <LoginButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => navigate('/')}
                >
                  {accessToken ? 'Back to Home' : 'Back to Login'}
                </LoginButton>
              </Grid>
            </LoginFormWrapper>
          </LoginContainer>
        </CommonPagesWrapper>
      </MainTheme>
    );
  }

  return (
    <MainTheme forceTheme='dark'>
      <CommonPagesWrapper>
        <LoginContainer>
          <LoginFormWrapper onSubmit={newPassApi}>
            <TextWrapper gap="8px">
              <Typography variant="dsm" color={(t) => t.palette.base.bodyText} fontWeight="600">
                  Set new password
              </Typography>
              <Typography variant="tmd" color={(t) => t.palette.grey[500]}>
                  Your new password must be different to previously used passwords.
              </Typography>
            </TextWrapper>
            <TextWrapper gap="16px" mt={'32px'}>
              {accessToken && (
                <CustomBasicInput
                  onChange={(e) => setCurrentPass(e.target.value)}
                  value={currentPass}
                  type={'password'}
                  placeholder='Current Password'
                  fullWidth
                />
              )}
              <CustomBasicInput
                onChange={handleOnChange}
                value={newPassword}
                type={'password'}
                placeholder={accessToken ? 'Set New Password' : 'Password'}
                fullWidth
              />
              <CustomBasicInput
                onChange={handleConfirmOnChange}
                value={newConfirm}
                placeholder={accessToken ? 'Confirm New Password' : 'Confirm password'}
                type={'password'}
                fullWidth
              />
            </TextWrapper>
            <Box mt={'24px'}>
              <Typography variant="tmd" color={(t) => t.palette.grey[500]} fontWeight="600">
                  Your new password must:
              </Typography>
              <StyledReqElement>
                {errors.digits ? <ClearIcon color="error" /> : <CheckIcon color="success" /> }
                <StyledReqText error={errors.digits}>
                    contain at least 1 digit
                </StyledReqText>
              </StyledReqElement>
              <StyledReqElement>
                {errors.upperCase ? <ClearIcon color="error" /> : <CheckIcon color="success" /> }
                <StyledReqText error={errors.upperCase}>
                    contain at least 1 upper case character
                </StyledReqText>
              </StyledReqElement>
              <StyledReqElement>
                {errors.specialCharacter ? <ClearIcon color="error" /> : <CheckIcon color="success" /> }
                <StyledReqText error={errors.specialCharacter}>
                    contain at least 1 special character
                </StyledReqText>
              </StyledReqElement>
              <StyledReqElement>
                {errors.lowerCase ? <ClearIcon color="error" /> : <CheckIcon color="success" /> }
                <StyledReqText error={errors.lowerCase}>
                    contain at least 3 lower case character
                </StyledReqText>
              </StyledReqElement>
              <StyledReqElement>
                {errors.length ? <ClearIcon color="error" /> : <CheckIcon color="success" /> }
                <StyledReqText error={errors.length}>
                    contain at least 8 character
                </StyledReqText>
              </StyledReqElement>
              <StyledReqElement>
                {!matched ? <ClearIcon color="error" /> : <CheckIcon color="success" /> }
                <StyledReqText error={!matched}>
                    should match pass
                </StyledReqText>
              </StyledReqElement>
            </Box>
            <Grid width={'100%'}
              item
              xs={12}
              mt={'24px'}>
              <LoginButton
                type="submit"
                disabled={!matched || !!Object.keys(errors).length}
                variant="contained"
                color="primary"
                fullWidth
                loading={loading}
                loadingPosition="end"
              >
                {accessToken ? 'Change Password' : 'Reset password'}
              </LoginButton>
            </Grid>
            <Grid
              width={'100%'}
              item
              xs={12}
              mt={'32px'}
            >
              <BackLoginText
                onClick={() => navigate('/')}
                variant="tsm"
                fontWeight="500"
              >
                <ArrowLeft />
                Back {!accessToken && 'to login'}
              </BackLoginText>
            </Grid>
          </LoginFormWrapper>
        </LoginContainer>
      </CommonPagesWrapper>
    </MainTheme>
  );
};

export default ResetPasswordConfirm;
